/**
 * SmartFarmMap — ラスター地図上のインタラクティブなオーバーレイマーカー
 * - ホバー・フォーカスでマーカーとリストをハイライト
 * - キーボード操作対応（Enter / Space）
 * - 選択時に `smartfarm:select` カスタムイベントを発火
 */
class SmartFarmMap {
  constructor() {
    this.map = document.getElementById('js-smartfarm-map');
    if (!this.map) return;

    this._markers = Array.from(this.map.querySelectorAll('.js-map-marker'));
    this._listItems = Array.from(document.querySelectorAll('.js-farm-list-item, .js-farm-area-list-item'));

    // 情報パネル要素（マーカーの data 属性から取得）
    this.infoPanel = document.getElementById('smartfarm-info-panel');
    this.infoName = document.getElementById('smartfarm-panel-name');
    this.infoSub = document.getElementById('smartfarm-panel-sub');
    this.infoDesc = document.getElementById('smartfarm-panel-desc');

    this.currentId = null;

    this._bind();

    // 外部クリックで選択解除
    document.addEventListener('click', (e) => {
      const isMarker = !!e.target.closest('.js-map-marker');
      const isList = !!(e.target.closest('.js-farm-list-item') || e.target.closest('.js-farm-area-list-item'));
      const isPanel = !!e.target.closest('.p-farm-area__map-info-panel');
      if (!isMarker && !isList && !isPanel) this._clearSelection();
    });

    // キーボード: Esc で選択解除
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this._clearSelection();
    });

    // 外部からクリアを要求するカスタムイベント
    document.addEventListener('smartfarm:clearAll', () => {
      this._clearSelection();
    });
  }

  _bind() {
    this._markers.forEach((marker) => {
      const id = marker.dataset.id;
      const listItems = Array.from(document.querySelectorAll(`.js-farm-list-item[data-id="${id}"], .js-farm-area-list-item[data-id="${id}"]`));

      marker.addEventListener('mouseenter', () => this._setActive(id));
      marker.addEventListener('focus', () => this._setActive(id));
      marker.addEventListener('mouseleave', () => this._clearActive());
      marker.addEventListener('blur', () => this._clearActive());

      marker.addEventListener('click', () => this._onSelect(id));
      marker.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._onSelect(id);
        }
      });

      if (listItems.length) {
        listItems.forEach((li) => {
          li.addEventListener('mouseenter', () => this._setActive(id));
          li.addEventListener('mouseleave', () => this._clearActive());
          li.addEventListener('click', () => this._onSelect(id));
        });
      }
    });
  }

  _clearActive() {
    // 一時的なホバー・アクティブ状態を解除し、選択中のアイテムはハイライトを維持
    this._markers.forEach((m) => m.classList.remove('is-active'));
    this._listItems.forEach((i) => {
      if (this.currentId && i.dataset.id === this.currentId) return;
      i.classList.remove('is-highlight');
    });
  }

  _setActive(id) {
    this._clearActive();
    const marker = this.map.querySelector(`.js-map-marker[data-id="${id}"]`);
    const items = Array.from(document.querySelectorAll(`.js-farm-list-item[data-id="${id}"], .js-farm-area-list-item[data-id="${id}"]`));
    marker?.classList.add('is-active');
    items.forEach(i => i.classList.add('is-highlight'));
  }

  _clearSelection() {
    this._markers.forEach((m) => {
      m.classList.remove('is-selected', 'pulsing');
      m.removeAttribute('aria-pressed');
    });
    this._listItems.forEach((i) => i.classList.remove('is-highlight'));
    if (this.infoPanel) {
      this.infoPanel.classList.remove('is-visible');
      this.infoPanel.setAttribute('aria-hidden', 'true');
    }
    this.currentId = null;
  }

  _onSelect(id) {
    // 同じ ID が選択済みの場合はトグル解除
    if (this.currentId === id) {
      this._clearSelection();
      return;
    }

    // 前の選択をクリア
    this._clearSelection();

    const marker = this.map.querySelector(`.js-map-marker[data-id="${id}"]`);
    const listItems = Array.from(document.querySelectorAll(`.js-farm-list-item[data-id="${id}"], .js-farm-area-list-item[data-id="${id}"]`));

    if (marker) {
      marker.classList.add('is-selected', 'pulsing');
      marker.setAttribute('aria-pressed', 'true');
      // アニメーション終了後に aria-pressed を解除
      setTimeout(() => marker.removeAttribute('aria-pressed'), 1200);

      // data 属性から情報パネルに値を反映
      if (this.infoPanel) {
        const title = marker.dataset.title || '';
        const location = marker.dataset.location || '';
        const desc = marker.dataset.desc || '';
        this.infoName && (this.infoName.textContent = title);
        this.infoSub && (this.infoSub.textContent = location);
        this.infoDesc && (this.infoDesc.textContent = desc);
        this.infoPanel.classList.add('is-visible');
        this.infoPanel.setAttribute('aria-hidden', 'false');
      }
    }

    listItems.forEach(i => i.classList.add('is-highlight'));

    // 現在の選択 ID を保持
    this.currentId = id;

    // 外部リスナー向けにカスタムイベントを発火
    document.dispatchEvent(new CustomEvent('smartfarm:select', { detail: { id } }));
  }
}

export default SmartFarmMap;
